import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Hyperlink API",
  "title": "Hyperlink",
  "props": ["HyperlinkProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const HyperlinkProps = makeShortcode("HyperlinkProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="hyperlink" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Links are used as navigational elements and can be used on their own or inline
  with text.
      </LeadParagraph>
      <h2>{`Usage Guidelines`}</h2>
      <h3>{`When to use`}</h3>
      <p>{`Use links when you want users to:`}</p>
      <ul>
        <li parentName="ul">{`Navigate to a different page within the application`}</li>
        <li parentName="ul">{`Navigate to an entirely different site`}</li>
        <li parentName="ul">{`Jump to an element on the same page`}</li>
        <li parentName="ul">{`Link to emails or phone numbers`}</li>
      </ul>
      <h3>{`When not to use`}</h3>
      <p>{`Use a button instead of a link for actions that will change data or manipulate how it is displayed, change a state, or trigger an action. Buttons should never be used for navigational actions.
See `}<Link to="/api/button" mdxType="Link">{`button docs`}</Link>{` for further information.`}</p>
      <h2>{`Basic Usage`}</h2>
      <p>{`Links provide a lightweight option for navigation but like other interactive elements, too many links will clutter a page and make it difficult for users to identify their next steps. This is especially true for inline links, which should be used sparingly.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Hyperlink } from 'react-magma-dom';

export function Example() {
  return (
    <Hyperlink target="_blank" to="https://www.google.com">
      Google
    </Hyperlink>
  );
}
`}</code></pre>
      <h2>{`StyledAs`}</h2>
      <p>{`Sometimes links can be styled as buttons in order to give them more weight on the page, but this is only on the surface. Underneath the styles, they are still a link component. The `}<inlineCode parentName="p">{`styledAs`}</inlineCode>{` prop can be used to style the `}<inlineCode parentName="p">{`Hyperlink`}</inlineCode>{` component as another component. The options are `}<inlineCode parentName="p">{`Link`}</inlineCode>{` (default) and `}<inlineCode parentName="p">{`Button`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonColor,
  ButtonTextTransform,
  Hyperlink,
  Spacer,
  SpacerAxis,
} from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Hyperlink
        textTransform={ButtonTextTransform.none}
        styledAs="Button"
        target="_blank"
        to="https://www.google.com"
      >
        Google
      </Hyperlink>
      <Spacer size="8" axis={SpacerAxis.horizontal} />
      <Hyperlink
        color={ButtonColor.secondary}
        textTransform={ButtonTextTransform.none}
        styledAs="Button"
        target="_blank"
        to="https://www.google.com"
      >
        Google
      </Hyperlink>
      <Spacer size="8" axis={SpacerAxis.horizontal} />
      <Hyperlink
        color={ButtonColor.danger}
        styledAs="Button"
        target="_blank"
        to="https://www.google.com"
      >
        Google
      </Hyperlink>
      <Spacer size="8" axis={SpacerAxis.horizontal} />
      <Hyperlink
        color={ButtonColor.marketing}
        styledAs="Button"
        target="_blank"
        to="https://www.google.com"
      >
        Google
      </Hyperlink>
    </>
  );
}
`}</code></pre>
      <h2>{`Icon`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Hyperlink,
  HyperlinkIconPosition,
  magma,
  Spacer,
  SpacerAxis,
  Paragraph,
  Flex,
  FlexBehavior,
  FlexJustify,
  TypographyVisualStyle,
} from 'react-magma-dom';
import {
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  CalendarTodayIcon,
  OpenInNewIcon,
} from 'react-magma-icons';

export function Example() {
  return (
    <>
      <Flex
        behavior={FlexBehavior.container}
        justify={FlexJustify.spaceBetween}
      >
        <span style={{ flex: '0 0 auto' }}>
          <Hyperlink
            target="_blank"
            to="./"
            hasUnderline={false}
            icon={<KeyboardArrowLeftIcon aria-hidden />}
            iconPosition={HyperlinkIconPosition.left}
          >
            Back
          </Hyperlink>
        </span>
        <span style={{ flex: '0 0 auto' }}>
          <Hyperlink
            target="_blank"
            to="./"
            hasUnderline={false}
            icon={<KeyboardArrowRightIcon aria-hidden />}
            iconPosition={HyperlinkIconPosition.right}
          >
            Next
          </Hyperlink>
        </span>
      </Flex>
      <Spacer size={'8px'} axis={SpacerAxis.both} />
      <Hyperlink
        styledAs="Button"
        target="_blank"
        to="./"
        icon={<KeyboardArrowLeftIcon aria-hidden />}
        iconPosition={HyperlinkIconPosition.left}
      >
        Back
      </Hyperlink>
      <Spacer size={'8px'} axis={SpacerAxis.horizontal} />
      <Hyperlink
        styledAs="Button"
        target="_blank"
        to="./"
        icon={[
          <KeyboardArrowLeftIcon aria-hidden key={0} />,
          <KeyboardArrowRightIcon aria-hidden key={1} />,
        ]}
        iconPosition={HyperlinkIconPosition.both}
      >
        Guess
      </Hyperlink>
      <Spacer size={'8px'} axis={SpacerAxis.horizontal} />
      <Hyperlink
        styledAs="Button"
        target="_blank"
        to="./"
        icon={<KeyboardArrowRightIcon aria-hidden />}
        iconPosition={HyperlinkIconPosition.right}
      >
        Next
      </Hyperlink>
      <Spacer size={'8px'} axis={SpacerAxis.both} />
      <Paragraph visualStyle={TypographyVisualStyle.headingSmall}>
        <Hyperlink
          target="_blank"
          to="https://www.google.com"
          hasUnderline={false}
          icon={[
            <CalendarTodayIcon
              key={0}
              size={magma.iconSizes.xLarge}
              style={{ marginRight: magma.spaceScale.spacing03 }}
              aria-hidden
            />,
            <OpenInNewIcon
              key={1}
              size={magma.iconSizes.xLarge}
              style={{ marginLeft: magma.spaceScale.spacing03 }}
              aria-hidden
            />,
          ]}
          iconPosition={HyperlinkIconPosition.both}
        >
          Schedule an appointment
        </Hyperlink>
      </Paragraph>
      <Paragraph visualStyle={TypographyVisualStyle.bodyLarge}>
        <Hyperlink
          target="_blank"
          to="https://www.google.com"
          hasUnderline={false}
          icon={[
            <CalendarTodayIcon
              key={0}
              size={magma.iconSizes.large}
              aria-hidden
            />,
            <OpenInNewIcon key={1} size={magma.iconSizes.large} aria-hidden />,
          ]}
          iconPosition={HyperlinkIconPosition.both}
        >
          Schedule an appointment
        </Hyperlink>
      </Paragraph>
      <Paragraph visualStyle={TypographyVisualStyle.bodySmall}>
        <Hyperlink
          target="_blank"
          to="https://www.google.com"
          hasUnderline={false}
          icon={[
            <CalendarTodayIcon
              key={0}
              size={magma.iconSizes.small}
              aria-hidden
            />,
            <OpenInNewIcon key={1} size={magma.iconSizes.small} aria-hidden />,
          ]}
          iconPosition={HyperlinkIconPosition.both}
        >
          Schedule an appointment
        </Hyperlink>
      </Paragraph>
      <Paragraph visualStyle={TypographyVisualStyle.bodyXSmall}>
        <Hyperlink
          target="_blank"
          to="https://www.google.com"
          hasUnderline={false}
          icon={[
            <CalendarTodayIcon
              key={0}
              size={magma.iconSizes.xSmall}
              aria-hidden
            />,
            <OpenInNewIcon key={1} size={magma.iconSizes.xSmall} aria-hidden />,
          ]}
          iconPosition={HyperlinkIconPosition.both}
        >
          Schedule an appointment
        </Hyperlink>
      </Paragraph>
    </>
  );
}
`}</code></pre>
      <h2>{`hasUnderline`}</h2>
      <p>{`The underline text decoration can be turned off. When a hyperlink is used within other elements like paragraphs, the underline is still mandatory.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Hyperlink,
  Spacer,
  SpacerAxis,
  magma,
  HyperlinkIconPosition,
} from 'react-magma-dom';
import { OpenInNewIcon } from 'react-magma-icons';

export function Example() {
  return (
    <>
      <Hyperlink
        target="_blank"
        to="https://www.google.com"
        hasUnderline={false}
      >
        Google
      </Hyperlink>
      <Spacer size={'8px'} axis={SpacerAxis.both} />
      Cupcake ipsum dolor sit amet wafer biscuit toffee. Chocolate bar brownie
      lemon drops tootsie roll pudding muffin powder pudding.{' '}
      <Hyperlink
        target="_blank"
        to="https://sallysbakingaddiction.com/triple-chocolate-layer-cake/"
        icon={<OpenInNewIcon size={magma.iconSizes.small} aria-hidden />}
        iconPosition={HyperlinkIconPosition.right}
      >
        I love chocolate cake
      </Hyperlink>{' '}
      Pastry dragée cheesecake chocolate bar donut jujubes candy canes sugar
      plum bonbon. Toffee pie macaroon apple pie gummi bears gummi bears
      shortbread.
    </>
  );
}
`}</code></pre>
      <h2>{`Render Props Usage`}</h2>
      <p>{`Use this when you want to render something that is from another library. For example, the `}<inlineCode parentName="p">{`Link`}</inlineCode>{` component from `}<inlineCode parentName="p">{`react-router`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Hyperlink } from 'react-magma-dom';

// We support Link components from libraries such as react-router/reach-router/gatsby that use the \`to\` prop in place of \`href\`
const Link = ({ to, ...rest }) => <a href={to} {...rest} />;

export function Example() {
  return (
    <Hyperlink styledAs="Button" to="./">
      {linkProps => <Link {...linkProps}>Render Props Google</Link>}
    </Hyperlink>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p><inlineCode parentName="p">{`isInverse`}</inlineCode>{` is an optional boolean prop, that may be passed in when the hyperlink is to be displayed on a dark background.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Card, CardBody, Hyperlink, Paragraph } from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Paragraph>
          <Hyperlink isInverse target="_blank" to="https://www.google.com">
            Google inverse link
          </Hyperlink>
        </Paragraph>
        <Hyperlink
          isInverse
          styledAs="Button"
          target="_blank"
          to="https://www.google.com"
        >
          Inverse link styled as button
        </Hyperlink>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Hyperlink Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`a`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a"
        }}>{`standard anchor attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`a`}</inlineCode>{` element that gets rendered.`}</p>
      <HyperlinkProps mdxType="HyperlinkProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      